import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const PythonCourseInNoida = () => {
  const testimonials = [
    {
      id: 1,
      name: "Aisha Sharma",
      review:
        "The Python training at Nadezhda Venture exceeded my expectations! The instructors made complex concepts of Data Science and Machine Learning easy to grasp. The hands-on projects were invaluable for building practical skills. Highly recommend for anyone aiming for a career in AI.",
      rating: 5,
      date: "2024-07-29",
      profileImg: "https://as1.ftcdn.net/jpg/02/81/82/46/1000_F_281824603_ge6QwIYZRtpQFDwAPD9T2BfZvnSPVVP1.jpg",
    },
    {
      id: 2,
      name: "Rahul Kumar",
      review:
        "I joined Nadezhda for Python full-stack development, and it was a great decision. The Flask framework module was especially well-taught, and I'm now confident in building web applications. Excellent placement support too!",
      rating: 4,
      date: "2024-07-28",
      profileImg: "https://i1.rgstatic.net/ii/profile.image/801823820623874-1568181104360_Q512/Rahul-Mondal-5.jpg",
    },
    {
      id: 3,
      name: "Priya Singh",
      review:
        "Nadezhda Venture is the best institute for Python in Noida. Their curriculum covers everything from basics to advanced topics like NumPy and Pandas, crucial for Data Analytics. The interactive sessions and doubt-clearing were very helpful.",
      rating: 5,
      date: "2024-07-27",
      profileImg: "https://www.sjc.ox.ac.uk/images/Priya_Urs.2e16d0ba.fill-312x312.jpg",
    },
    {
      id: 4,
      name: "Gaurav Mehta",
      review:
        "I completed my Python training here and got placed as a Junior Data Analyst. The focus on real-world scenarios and practical coding helped me immensely. The trainers are highly experienced and supportive.",
      rating: 4,
      date: "2024-07-26",
      profileImg: "https://www.esm.rochester.edu/uploads/Gaurav-Sarangi-crop.jpg",
    },
    {
      id: 5,
      name: "Sneha Reddy",
      review:
        "Fantastic Python course! I learned so much about object-oriented programming in Python and how to use it for various applications. The learning environment is very conducive to growth.",
      rating: 5,
      date: "2024-07-25",
      profileImg: "https://media.licdn.com/dms/image/v2/D4E03AQHLA9ujqZgrWQ/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1693791406881?e=2147483647&v=beta&t=SQhYXsKFGDRGkJ--Ql3OerEIcTyN2BpVbZDKbmyL8JA",
    },
    {
      id: 6,
      name: "Vikas Sharma",
      review:
        "The best place for Python training in Noida. The faculty's expertise is commendable, and the personalized attention helped me grasp complex concepts easily. Highly recommend it for aspiring Python developers.",
      rating: 5,
      date: "2024-07-29",
      profileImg: "https://fadcdn.s3.ap-south-1.amazonaws.com/media/1330/Lead_image_68172.jpg",
    },
    {
      id: 7,
      name: "Deepak Kumar",
      review:
        "I got placed right after completing my Python course from Nadezhda. The mock interviews and resume building sessions were extremely helpful. Thank you for the excellent support!",
      rating: 4,
      date: "2024-07-28",
      profileImg: "https://pbs.twimg.com/profile_images/1515018130594041859/KLBnjItE_400x400.jpg",
    },
  ];

  const faqContent = [
    {
      title: "What is Python and why should I learn it?",
      answer:
        "Python is a high-level, interpreted, general-purpose programming language. It is known for its simplicity, readability, and versatility. Learning Python opens doors to careers in Web Development (Django, Flask), Data Science, Machine Learning, Artificial Intelligence, Automation, and more, due to its vast libraries and frameworks.",
    },
    {
      title: "What are the prerequisites for joining Python training?",
      answer:
        "There are no strict programming prerequisites. A basic understanding of computer operations and logical thinking is helpful. Our course starts from the absolute basics, making it suitable for beginners as well as those with some prior programming experience.",
    },
    {
      title: "What topics are covered in your Python course?",
      answer:
        "Our comprehensive Python course covers Python fundamentals, data structures, object-oriented programming, file I/O, error handling, modules, and popular libraries like NumPy, Pandas (for data analysis), Matplotlib (for data visualization), and an introduction to web frameworks (Django/Flask) or machine learning libraries (Scikit-learn, TensorFlow/PyTorch) depending on the specialization track.",
    },
    {
      title: "How is the Python training delivered (online/offline)?",
      answer:
        "We offer flexible training modes including intensive offline classroom sessions at our Noida center and interactive online classes. Both modes feature live instructor-led sessions, practical exercises, and project-based learning.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Yes, we provide dedicated placement assistance to our Python graduates. This includes resume building, interview preparation, mock interviews, and connecting you with our network of hiring partners in Noida and across India.",
    },
    {
      title: "What kind of projects will I work on during the training?",
      answer:
        "You will work on multiple hands-on projects, ranging from basic Python scripting challenges to complex applications. This could include a simple command-line game, a data analysis script, a small web application, or a machine learning model, depending on your chosen specialization.",
    },
    {
      title: "Is there a certification provided after the Python course?",
      answer:
        "Yes, upon successful completion of the Python training and project work, you will receive a course completion certificate from Appwars Technologies, which is recognized by industry professionals.",
    },
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const curriculumContent = [
    {
      title: "Module 1: Python Fundamentals & Basics",
      description: "Introduction to Python, Python installation, data types, variables, operators, conditional statements (if-else), loops (for, while), functions, and basic I/O operations.",
    },
    {
      title: "Module 2: Data Structures in Python",
      description: "Understanding and working with Lists, Tuples, Dictionaries, Sets. Hands-on exercises for manipulating and iterating through these structures effectively.",
    },
    {
      title: "Module 3: Object-Oriented Programming (OOP) in Python",
      description: "Concepts of classes, objects, inheritance, polymorphism, encapsulation, and abstraction. Designing and implementing object-oriented solutions in Python.",
    },
    {
      title: "Module 4: File Handling & Exception Handling",
      description: "Reading from and writing to files (text, CSV, JSON). Handling runtime errors gracefully using try-except blocks, custom exceptions.",
    },
    {
      title: "Module 5: Modules, Packages & Regular Expressions",
      description: "Creating and importing custom modules, understanding package structure. Introduction to regular expressions for pattern matching and text manipulation.",
    },
    {
      title: "Module 6: Introduction to Web Development with Python (Flask/Django)",
      description: "Basics of web frameworks, setting up a development environment, routing, templates, handling forms, and building simple web applications.",
    },
    {
      title: "Module 7: Data Analysis with NumPy & Pandas",
      description: "Working with numerical data using NumPy arrays. Data manipulation, cleaning, and analysis using Pandas DataFrames and Series. Practical data cleaning and transformation techniques.",
    },
    {
      title: "Module 8: Data Visualization with Matplotlib & Seaborn",
      description: "Creating various types of plots (line, bar, scatter, histogram) for data visualization. Customizing plots for effective communication of insights.",
    },
    {
      title: "Module 9: Introduction to Machine Learning (Scikit-learn)",
      description: "Fundamentals of Machine Learning, supervised vs. unsupervised learning. Implementing basic ML algorithms like Linear Regression, Logistic Regression, Decision Trees using Scikit-learn.",
    },
    {
      title: "Module 10: Advanced Topics & Project Work",
      description: "Best practices in Python, debugging techniques, virtual environments. Comprehensive capstone project integrating learned concepts to solve a real-world problem.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2;  // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleTestimonialsCount());

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  // Logic to handle showing multiple cards and wrapping around
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(testimonials[(startIndex + i) % testimonials.length]);
  }


  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Python Training in Noida | Expert Python Course with Certification</title>
        <meta
          name="description"
          content="Master Python with our comprehensive training in Noida. Learn Data Science, Machine Learning, Web Development with hands-on projects and placement support."
        />
        <meta
          name="keywords"
          content="Python training Noida, Python course, Python certification, Python coaching Noida, Data Science, Machine Learning, Web Development Python, AI training"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://img.freepik.com/premium-photo/glowing-blue-snake-made-code-screen-with-word-python-highlighted_1294789-14756.jpg?semt=ais_hybrid&w=740" // Using the same placeholder image
          alt="Python Training Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
         {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

       <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
               <div className="md:w-1/2">
                 <motion.h1
                   initial={{ opacity: 0, y: 30 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ duration: 0.8 }}
                   className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
                 >
                   <span className="text-orange-600">Python</span> Training in Noida
                 </motion.h1>
       
                 <motion.p
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.3, duration: 0.8 }}
                   className="mt-6 text-lg md:text-xl text-gray-200 text-left"
                 >
                   Join Nadezhda Venture for expert-led Python Training in Noida. Build real-world skills with hands-on projects and certification support.
                 </motion.p>
       
                 <motion.button
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.6, duration: 0.8 }}
                   className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
                 >
                   <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Python Development Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
                 </motion.button>
               </div>
               </div>
      </section>

      {/* Why Python Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Learn Python?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            Python is the most in-demand programming language today, powering fields from web development to artificial intelligence. Its simplicity, versatility, and vast ecosystem of libraries make it an indispensable skill for modern tech professionals.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">Versatile & Powerful</h3>
              <p className="text-gray-600">Used in Web Development, Data Science, AI/ML, Automation, and more.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">Simple & Readable Syntax</h3>
              <p className="text-gray-600">Easier to learn for beginners, fostering faster development.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">Huge Community & Libraries</h3>
              <p className="text-gray-600">Access to extensive resources and powerful third-party modules.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🚀', title: 'Beginner-Friendly Curriculum', desc: 'Starts from basics, no prior coding experience needed.' },
              { icon: '💡', title: 'Hands-on Project Based Learning', desc: 'Work on real-world projects to build a strong portfolio.' },
              { icon: '👨‍🏫', title: 'Experienced Industry Trainers', desc: 'Learn from experts with years of practical Python experience.' },
              { icon: '🎓', title: 'Globally Recognized Certification', desc: 'Boost your resume with our industry-aligned course certificate.' },
              { icon: '🤝', title: 'Dedicated Placement Assistance', desc: 'Get support for resume building, mock interviews, and job opportunities.' },
              { icon: '🔄', title: 'Flexible Learning Options', desc: 'Choose between offline classroom or interactive online sessions.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-blue-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-blue-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Technologies You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our Python training ensures you gain proficiency in the essential tools and libraries used in various Python domains.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
              { name: 'Jupyter', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jupyter/jupyter-original-wordmark.svg' },
              { name: 'NumPy', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/numpy/numpy-original.svg' },
              { name: 'Pandas', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pandas/pandas-original.svg' },
              { name: 'Matplotlib', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/matplotlib/matplotlib-original.svg' },
              { name: 'Scikit-learn', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Scikit_learn_logo_small.svg/1200px-Scikit_learn_logo_small.svg.png' },
              { name: 'Django', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/django/django-plain.svg' },
              { name: 'Flask', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flask/flask-original.svg' },
              { name: 'TensorFlow', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg' },
              { name: 'Keras', logo: 'https://w7.pngwing.com/pngs/571/118/png-transparent-keras-logo-thumbnail.png' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A Python certification from Appwars Technologies can unlock a wide array of high-demand job roles across various industries.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '📊', title: 'Data Scientist', desc: 'Analyze complex data to extract insights and build predictive models.' },
              { icon: '🤖', title: 'Machine Learning Engineer', desc: 'Design, build, and deploy machine learning systems.' },
              { icon: '🌐', title: 'Python Web Developer', desc: 'Develop robust and scalable web applications using Django/Flask.' },
              { icon: '📈', title: 'Data Analyst', desc: 'Interpret data, analyze results using statistical techniques.' },
              { icon: '⚙️', title: 'DevOps Engineer', desc: 'Automate software development processes and manage infrastructure.' },
              { icon: '💻', title: 'Software Engineer (Python)', desc: 'Develop, test, and deploy software solutions using Python.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-blue-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Course Content / FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-blue-300 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">Enquire Now</h3>
            <p className="text-center text-gray-600 mb-6">Fill out the form below to get more details about our Python training program.</p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Python Training)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-900">Our Students Say</h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }} // Initial position based on index in visible set
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-blue-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">{testimonial.date}</p>
                        </div>
                
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a href="#" className="text-blue-600 hover:underline mt-4 text-sm text-right">
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  // Logic to highlight dots based on the current set of visible testimonials
                  idx >= currentTestimonialIndex && idx < currentTestimonialIndex + visibleCount
                    ? "bg-blue-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

       {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default PythonCourseInNoida;